import { Heading, Information, Title2 } from 'components/Typography';
import { Button } from 'components/Button';
import React, { useEffect, useState, useRef } from 'react';
import { Col, Progress, Row, Table, Popover, Spin } from 'antd';
import { CheckCircleOutlined, DeleteOutlined, EditOutlined, DoubleLeftOutlined, DoubleRightOutlined } from '@ant-design/icons';
import MultipleLeaveDeleteModal from 'pages/profile/multipleLeaveDeleteModal';
import LeaveRequestsModal from 'pages/profile/leaveRequestsModal';
import RequestsConfirmationModal from 'pages/attendanceNew/Modals/requestsConfirmationModal';
import { connect } from 'react-redux';
import { leaveOrAttendanceRequestActions } from 'actions/userManagementV3';
import { deleteHistory, getLeavesData, getLeavesTypes } from 'actions/userManagementV3';
import moment from 'moment';
import { FiChevronDown } from 'react-icons/fi';
import UserAvatar from 'components/userAvatar';
import EditApproversDrawer from 'pages/attendanceNew/Modals/editApproversDrawer';
import { searchUsers } from 'actions/userManagementV2';
import { postEditApprovers } from 'actions/attendanceNew';
import EditLeaveDrawer from './localComponent/editLeaveDrawer';
import MarkLeaveDrawer from './localComponent/markLeaveDrawer';
import ConfirmLeaveDelete from 'pages/profile/confirmLeaveDelete';
import { Api, Color } from 'services';
import LeaveTable from './localComponent/LeaveTable';
import './profileLeavesStyles.css';
import PageDataLoader from 'components/pageDataLoader';

const hasLeavePrivilege = (user) => {
  const priv = user?.privilage || user?.privilege || user?.privilages;
  if (Array.isArray(priv)) {
    return priv.includes('SuperAdmin') || priv.includes('LeaveManager');
  }
  if (typeof priv === 'string') {
    return priv.includes('SuperAdmin') || priv.includes('LeaveManager');
  }
  return false;
};

const isPastLeaveRecord = (record) => {
  if (Array.isArray(record?.requests) && record.requests.length > 0) {
    return record.requests.every(req => {
      const d = req?.date || req?.startDate;
      if (!d) return false;
      return moment(d, ['YYYY-MM-DD', 'DD/MM/YYYY', moment.ISO_8601]).startOf('day').isBefore(moment().startOf('day'));
    });
  }

  const checkDate = record?.endDate || record?.startDate;
  if (!checkDate) return false;
  return moment(checkDate, ['YYYY-MM-DD', 'DD/MM/YYYY', moment.ISO_8601]).startOf('day').isBefore(moment().startOf('day'));
};

// True only when every day of the leave is after today (tomorrow onwards) —
// approved / rejected leaves can be changed only then.
const isFutureLeaveRecord = (record) => {
  const today = moment().startOf('day');
  const parse = (d) => moment(d, ['YYYY-MM-DD', 'DD/MM/YYYY', moment.ISO_8601]).startOf('day');
  if (Array.isArray(record?.requests) && record.requests.length > 0) {
    return record.requests.every(req => {
      const d = req?.date || req?.startDate;
      return Boolean(d) && parse(d).isAfter(today);
    });
  }
  const checkDate = record?.startDate || record?.endDate;
  if (!checkDate) return false;
  return parse(checkDate).isAfter(today);
};

const columns = (
  setDeleteLeaveModal,
  handleEditLeave,
  selectedRowKeys,
  setIdToDelete,
  user,
  setMultiLeaveDeleteModal,
  setActionObject,
  setReqTitleModal,
  toggleConfirmationModal,
  leaveOrAttendanceRequestActions,
  fromWhere,
  setSelectedLeaveRow,
  setIsEdit
) => {
  const canPerformActions = hasLeavePrivilege(user);

  const tableColumns = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: 50,
      align: 'left',
      render: (text, record, index) => (
        <div style={{ display: 'flex', justifyContent: 'flex-start', fontFamily: 'Roboto', textAlign: 'left' }}>
          {/* <Title2>{index + 1}</Title2> */}
          {index + 1}
        </div>
      ),
    },

    {
      title: 'DATES',
      dataIndex: 'dates',
      key: 'dates', width: 180,
      className: 'leave-date-column',
      render: (value, record) => {
        const startDate = moment(record.startDate, 'YYYY-MM-DD');
        const endDate = moment(record.endDate, 'YYYY-MM-DD');
        const duration = endDate.diff(startDate, 'days') + 1;

        return (
          <div style={{ display: 'flex', justifyContent: 'flex-start', fontFamily: 'Roboto', textAlign: 'left' }}>
            {/* <Title2> */}
            {duration <= 1
              ? startDate.format('DD/MM/YYYY')
              : `${startDate.format('DD/MM/YYYY')} - ${endDate.format('DD/MM/YYYY')}`}
            {/* </Title2> */}
          </div>
        );
      },
    },
    {
      title: 'DURATION',
      dataIndex: 'duration',
      key: 'duration', width: 100,
      render: (value) => {
        const num = Number(value);
        const durationText = (!value && value !== 0) ? '-' : num > 1 ? `${value} days` : `${value} day`;
        return (
          <div style={{ display: 'flex', justifyContent: 'flex-start', fontFamily: 'Roboto', textAlign: 'left' }}>
            {durationText}
          </div>
        );
      },
    },
    {
      title: 'LEAVE TYPE',
      dataIndex: 'leaveType',
      key: 'leaveType', width: 100,
      render: (value) => (
        <div style={{ display: 'flex', justifyContent: 'flex-start', fontFamily: 'Roboto', textAlign: 'left' }}>
          {/* <Title2>{value} leave</Title2> */}
          {value}
        </div>
      ),
    },
    {
      title: 'STATUS',
      key: 'status', width: 130,
      render: (value, record) => {
        let statusClass = 'status-unknown';
        const currentStatus = record?.overAllStatus ?? record?.overallStatus ?? record?.status ?? value;
        const normalized = typeof currentStatus === 'string' ? currentStatus.trim().toLowerCase() : '';

        switch (normalized) {
          case 'approved':
            statusClass = 'status-approved';
            break;
          case 'pending':
            statusClass = 'status-pending';
            break;
          case 'partially approved':
          case 'partially_approved':
            statusClass = 'status-partially-approved';
            break;
          case 'partially rejected':
          case 'partially_rejected':
            statusClass = 'status-partially-rejected';
            break;
          case 'rejected':
            statusClass = 'status-rejected';
            break;
          default:
            statusClass = 'status-unknown';
        }

        return (
          <div className={`status-container ${statusClass}`}>
            {currentStatus
              ? String(currentStatus).replace(/_/g, ' ').toUpperCase()
              : statusClass.replace('status-', '').replace('-', ' ').toUpperCase()}
          </div>
        );
      },
    },
  ];

  if (canPerformActions) {
    tableColumns.push({
      title: 'Actions', width: 240,
      className: 'leave-date-column',
      render: (value, record) => {
        const currentStatus = record?.overAllStatus ?? record?.overallStatus ?? record?.status;
        const normalizedStatus = typeof currentStatus === 'string' ? currentStatus.trim().toLowerCase() : '';
        const isPartiallyApprovedOrRejected =
          normalizedStatus === 'partially approved' ||
          normalizedStatus === 'partially_approved' ||
          normalizedStatus === 'partially rejected' ||
          normalizedStatus === 'partially_rejected';
        const isPast = isPastLeaveRecord(record);
        const isFuture = isFutureLeaveRecord(record);

        const hasPendingItem = Array.isArray(record?.requests) && record.requests.some(req => {
          const s = String(req?.overAllStatus ?? req?.overallStatus ?? req?.status ?? '').trim().toLowerCase();
          return s === 'pending';
        });

        const showPendingButtons = normalizedStatus === 'pending' || (isPartiallyApprovedOrRejected && (!isPast || hasPendingItem));

        return (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            {showPendingButtons && (
              <>
                <div style={{ display: 'flex', justifyContent: "flex-start", width: '100%' }}>
                  <Button style={{ textAlign: 'left', color: '#FF414D', fontWeight: 700 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActionObject({ type: 'reject', val: record });
                      setReqTitleModal('Are you sure you want to reject?');
                      toggleConfirmationModal(true);
                    }}
                    type="transparent"
                  >
                    REJECT
                  </Button>
                  <Button
                    style={{ textAlign: 'left', color: '#00A566', fontWeight: 700 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActionObject({ type: 'approve', val: record });
                      setReqTitleModal('Are you sure you want to approve?');
                      toggleConfirmationModal(true);
                    }}
                    type="transparent"
                  >
                    APPROVE
                  </Button>
                </div>
              </>
            )}
            {isFuture && normalizedStatus === 'approved' && record?.requestType === 'Leave' && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', }}>
                <Button
                  style={{ alignItems: 'center', color: '#FF414D', fontWeight: 700 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActionObject({ type: 'change to rejected', val: record });
                    setReqTitleModal('Are you sure you want to reject?');
                    toggleConfirmationModal(true);
                  }}
                  type="transparent"
                >
                  CHANGE TO REJECTED
                </Button>
              </div>
            )}
            {isFuture && normalizedStatus === 'rejected' && record?.requestType === 'Leave' && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', }}>
                <Button
                  style={{ alignItems: 'center', color: '#00A566', fontWeight: 700 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActionObject({ type: 'change to approved', val: record });
                    setReqTitleModal('Are you sure you want to approve?');
                    toggleConfirmationModal(true);
                  }}
                  type="transparent"
                >
                  CHANGE TO APPROVED
                </Button>
              </div>
            )}
          </div>
        );
      },
    });
  }

  return tableColumns;
};

const profileLeaves = ({ getLeavesData, leaveDetails_V3, leaveTypes, user, getLeavesTypes, searchedTeachers, searchUsers, postEditApprovers, othersSelectedTab, deleteHistory, specificUserId, leaveOrAttendanceRequestActions, fromWhere, specificUserProfile, hideApprovers, yearOnly }) => {

  const [markLeaveModal, setMarkLeaveModal] = useState(false);
  const [deleteLeaveModal, setDeleteLeaveModal] = useState(false);
  const [editLeaveModal, setEditLeaveModal] = useState(false);
  const [leaveSummaryData, setLeaveSummaryData] = useState([[], []]);
  const [multiLeaveDeleteModal, setMultiLeaveDeleteModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [selectedLeaveRow, setSelectedLeaveRow] = useState([]);
  const [actionObject, setActionObject] = useState({ type: '', val: '' });
  const [openLeaveRequestModal, setLeaveRequestModal] = useState(false);
  const [reqModalTitle, setReqTitleModal] = useState('');
  const [openConfirmationModal, toggleConfirmationModal] = useState(false);
  const [approversList, setApproversList] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [editApproverModal, setEditApproverModal] = useState(false);
  const currentYear = moment().year();
  const currentMonthIndex = moment().month();

  const [isMonth, setIsMonth] = useState(moment().format('MMM'));
  const [isYear, setIsYear] = useState(moment().format('YYYY'));
  const [monthOpen, setMonthOpen] = useState(false);
  const [yearOpen, setYearOpen] = useState(false);
  const [decadeStart, setDecadeStart] = useState(() => {
    const y = Number(moment().format('YYYY')) || 2026;
    return Math.floor(y / 10) * 10;
  });

  const yearWrapperRef = useRef(null);
  const monthWrapperRef = useRef(null);

  useEffect(() => {
    if (!yearOpen && !monthOpen) return;
    const handleScroll = () => {
      window.dispatchEvent(new Event('resize'));
    };
    const scrollContainers = document.querySelectorAll('.staff-profile-info-scroll, .dv2-outer-scroll, #contentWrapper');
    scrollContainers.forEach((c) => c.addEventListener('scroll', handleScroll, { passive: true }));
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      scrollContainers.forEach((c) => c.removeEventListener('scroll', handleScroll));
      window.removeEventListener('scroll', handleScroll);
    };
  }, [yearOpen, monthOpen]);

  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const yearItems = [];
  for (let i = -1; i <= 10; i++) {
    yearItems.push(decadeStart + i);
  }

  const isNextDecadeDisabled = (decadeStart + 10) > currentYear;

  const handleYearSelect = (y) => {
    if (y > currentYear) return;
    setIsYear(String(y));
    setYearOpen(false);
    if (y === currentYear) {
      const selectedMonthIdx = moment(isMonth, 'MMM').month();
      if (selectedMonthIdx > currentMonthIndex) {
        setIsMonth(moment().format('MMM'));
      }
    }
  };

  const isMonthDisabled = (monthIndex) => {
    const selectedYear = Number(isYear);
    if (selectedYear > currentYear) return true;
    if (selectedYear === currentYear) {
      return monthIndex > currentMonthIndex;
    }
    return false;
  };

  const handleMonthSelect = (m, idx) => {
    if (isMonthDisabled(idx)) return;
    setIsMonth(m);
    setMonthOpen(false);
  };

  const monthPopoverContent = (
    <div className="leave-month-popover-card">
      <div className="leave-picker-body">
        <div className="leave-picker-grid">
          {MONTHS.map((m, idx) => {
            const isSelected = isMonth === m;
            const isDisabled = isMonthDisabled(idx);
            return (
              <div
                key={m}
                className={`leave-picker-cell ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                onClick={() => {
                  if (!isDisabled) {
                    handleMonthSelect(m, idx);
                  }
                }}
              >
                {m}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const yearPopoverContent = (
    <div className="leave-year-popover-card">
      <div className="leave-picker-header">
        <DoubleLeftOutlined
          className="leave-picker-nav-btn"
          onClick={(e) => {
            e.stopPropagation();
            setDecadeStart((prev) => prev - 10);
          }}
        />
        <span className="leave-picker-header-title">
          {decadeStart}-{decadeStart + 9}
        </span>
        <DoubleRightOutlined
          className={`leave-picker-nav-btn ${isNextDecadeDisabled ? 'disabled' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            if (!isNextDecadeDisabled) {
              setDecadeStart((prev) => prev + 10);
            }
          }}
        />
      </div>
      <div className="leave-picker-body">
        <div className="leave-picker-grid">
          {yearItems.map((y) => {
            const isOutOfDecade = y < decadeStart || y > decadeStart + 9;
            const isFutureYear = y > currentYear;
            const isSelected = String(y) === String(isYear);
            return (
              <div
                key={y}
                className={`leave-picker-cell ${isOutOfDecade ? 'other-decade' : ''} ${isSelected ? 'selected' : ''} ${isFutureYear ? 'disabled' : ''}`}
                onClick={() => {
                  if (!isFutureYear) {
                    handleYearSelect(y);
                  }
                }}
              >
                {y}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
  const [leaveFormDetails, setLeaveDetails] = useState({
    leaveType: null,
    subType: null,
    startDate: null,
    endDate: null,
    duration: null,
    reason: null
  });
  console.log("leavesTypesconsoled", leaveTypes);
  console.log("SpecificUserProfile", specificUserProfile);
  const [isEdit, setIsEdit] = useState(false);
  const [pageLoader, setPageLoader] = useState(false);
  const [tableLoader, setTableLoader] = useState(false);
  const isInitialMount = useRef(true);
  const prevProfileIdRef = useRef(null);
  const selectedProfileId = specificUserProfile?.id || specificUserId || user?.id;

  const fetchLeaves = (targetMonth = isMonth, targetYear = isYear, isTableOnly = false) => {
    if (!selectedProfileId) return;
    if (isTableOnly) {
      setTableLoader(true);
    } else {
      setPageLoader(true);
    }
    const monthNum = !isNaN(Number(targetMonth)) ? Number(targetMonth) : (moment(targetMonth, 'MMM').month() + 1);
    const yearNum = Number(targetYear) || moment().year();
    const params = {
      userId: selectedProfileId,
      year: yearNum
    };
    // yearOnly (Users V2): the whole year — no month param.
    if (!yearOnly) params.month = monthNum;
    getLeavesData(params, () => {
      setTableLoader(false);
      if (isTableOnly) {
        // table loader done
      } else {
        setPageLoader(false);
      }
    });
  };

  useEffect(() => {
    const isProfileChange = prevProfileIdRef.current !== selectedProfileId;
    prevProfileIdRef.current = selectedProfileId;

    if (isInitialMount.current || isProfileChange) {
      isInitialMount.current = false;
      fetchLeaves(isMonth, isYear, false);
    } else {
      fetchLeaves(isMonth, isYear, true);
    }
  }, [selectedProfileId, isMonth, isYear]);

  useEffect(() => {
    setTableLoader(false);
    if (leaveDetails_V3?.leaveSummary) {
      for (let i = 0; i < 3; i++) {
        leaveSummaryData[0][i] = leaveDetails_V3?.leaveSummary[i]
      }
      for (let i = 3; i < 6; i++) {
        leaveSummaryData[1][i - 3] = leaveDetails_V3?.leaveSummary[i]
      }
      setLeaveSummaryData([...leaveSummaryData])
      // console.log("leaveSummaryDataconsoled" ,leaveSummaryData);
    }
    if (leaveDetails_V3?.allApprovers) {
      const approversData = leaveDetails_V3?.allApprovers.map((approver, index) => ({
        id: approver.id,
        name: approver.name,
        role: approver.role.join(', '),
        phone: approver.phone,
        image: approver.image,
        index: index + 1 // Adding index for the label (Approver 1, Approver 2, etc.)
      }));
      setApproversList(approversData);
    }
  }, [leaveDetails_V3]);

  useEffect(() => {
    if (tableLoader) {
      const timer = setTimeout(() => {
        setTableLoader(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [tableLoader]);

  const disablePrevDateRows = (record) => {
    const startDate = moment(record?.startDate, 'YYYY-MM-DD');
    const currentDate = moment().startOf('day');
    return startDate.isBefore(currentDate);
  };

  const rowSelection = {
    type: "checkbox",
    selectedRowKeys: selectedRowKeys,
    getCheckboxProps: (record) => ({ disabled: disablePrevDateRows(record) }),
    onChange: (selectedRowKeys, selections) => {
      setSelectedRows([...selections?.map(item => item?.fileKey)]);
      setSelectedRowKeys([...selectedRowKeys])
    },
  };
  const handleApprove = (commonId, requestId) => {
    let params = {
      action: 'Approved',
      commonId: commonId == null ? [] : [commonId],
      requestIds: requestId == null ? [] : requestId?.length > 1 ? requestId : requestId?.length == 0 ? [] : [requestId],
      requestType: 'Leave',
      userId: selectedProfileId
    };

    const successCallback = () => {
      toggleConfirmationModal(false);
      fetchLeaves();
    };

    setPageLoader(true);
    leaveOrAttendanceRequestActions(params, () => setPageLoader(false), setPageLoader, successCallback);
  };

  const handleReject = (commonId, requestId) => {
    let params = {
      action: 'Rejected',
      commonId: commonId == null ? [] : [commonId],
      requestIds: requestId == null ? [] : requestId?.length > 1 ? requestId : requestId?.length == 0 ? [] : [requestId],
      requestType: 'Leave',
      userId: selectedProfileId
    };
    const successCallback = () => {
      toggleConfirmationModal(false);
      fetchLeaves();
    };

    setPageLoader(true);
    leaveOrAttendanceRequestActions(params, () => setPageLoader(false), setPageLoader, successCallback);
  };
  const handleChangeStatus = (status, commonId) => {
    // setSelectedCommonId(commonId);
    let params = {
      action: status,
      commonId: [commonId],
      requestIds: [],
      requestType: 'Leave',
      userId: selectedProfileId
    };
    const successCallback = () => {
      toggleConfirmationModal(false);
      fetchLeaves();
    };
    setPageLoader(true);
    leaveOrAttendanceRequestActions(params, () => setPageLoader(false), setPageLoader, successCallback);
  };
  const handleReqActionButton = (e) => {
    toggleConfirmationModal(false);
    switch (actionObject?.type) {
      case 'reject':
        handleReject(actionObject?.val?.commonId, actionObject?.val?.id);
        break;

      case 'approve':
        console.log("approving Leave")
        handleApprove(actionObject?.val?.commonId, actionObject?.val?.id);
        break;

      case 'change to rejected':
        handleChangeStatus('Rejected', actionObject?.val?.commonId);
        break;

      case 'change to approved':
        handleChangeStatus('Approved', actionObject?.val?.commonId);
        break;

      default:
        break;
    }
  }
  const handleEditLeave = (record) => {
    setMarkLeaveModal(true);
    setLeaveDetails({ ...record, startDate: moment(record?.startDate), endDate: moment(record?.endDate), subType: record.duration === 0.5 ? "half-day" : "full-day" })
  }

  const handleCloseMarkLeaveModal = (isSaved = false) => {
    setMarkLeaveModal(false);
    setIsEdit(false);
    setLeaveDetails({
      leaveType: null,
      subType: null,
      startDate: null,
      endDate: null,
      duration: null,
      reason: null
    });
    if (isSaved === true) {
      setTableLoader(true);
    }
  };

  const setIdToDelete = (id) => {
    selectedRowKeys.push(id);
    setSelectedRowKeys([...selectedRowKeys]);
  };

  const successCallback = () => {
    setDeleteLeaveModal(false);
    setSelectedRowKeys([]);
    fetchLeaves();
  }

  const handleDelete = () => {
    let params = {
      commonId: selectedRowKeys
    }
    setDeleteLoader(true)
    deleteHistory(params, () => setDeleteLoader(false), () => successCallback(), 'LeaveSummary');
  }

  const onRow = (record) => {
    return {
      style: { cursor: 'pointer' },
      onClick: () => {
        setSelectedLeaveRow(record);
        setLeaveRequestModal(true);
      }
    }
  };

  return (
    <div className='p-l-30 p-r-20'>
      <div className='r-jsb p-15'>
        <Heading>Overview</Heading>
        <div className='r-ac'>
          {((user?.privilage?.includes('SuperAdmin') || user?.privilage?.includes('LeaveManager')) || !user?.role?.includes("Student")) ?
            <>
              {(user?.privilage?.includes('SuperAdmin') || user?.privilage?.includes('LeaveManager')) && <Button type="link" style={{ marginRight: 20, fontSize: 14 }} onClick={() => { setEditLeaveModal(true); }} icon={<EditOutlined />}>EDIT</Button>}
            </>
            : null
          }
        </div>
      </div>
      {leaveSummaryData.map((row, i) => (
        <Row key={i} style={{ marginBottom: i < leaveSummaryData.length - 1 ? 24 : 0 }}>
          <>
            {row.map((summary, index) => (
              summary ? (
                <Col md={{ span: 8 }} sm={{ span: 12 }} key={index}>
                  <div style={{ width: "90%", border: "1px solid #E6E6E6" }} className="p-20 r-jsb border-20">
                    <div style={{ width: "100%" }}>
                      <Title2 >{summary?.leaveType} Leave</Title2>
                      <div className="r-jsb m-t-15 simgrey" style={{ width: "70%" }}>
                        <Information>Balance</Information>
                        <Information>{summary.remaining}</Information>
                      </div>
                      <div className="r-jsb m-t-8 simgrey" style={{ width: "70%" }}>
                        <Information>Availed</Information>
                        <Information>{summary.availed}</Information>
                      </div>
                    </div>
                    <div style={{ width: "45%" }} className="m-t-10">
                      <Progress
                        type="circle"
                        percent={summary?.total ? (summary?.remaining / summary?.total) * 100 : 0}
                        width={84}
                        strokeWidth={8}
                        strokeColor="#00A566"
                        trailColor="#E6E6E6"
                        format={(percent) => (
                          <div>
                            <Information className="m-t-5 color-black">{summary?.total ?? 0}</Information>
                            <div> <Information>total</Information></div>
                          </div>
                        )}
                      />
                    </div>
                  </div>
                </Col>

              ) : null
            ))}
          </>
        </Row>
      ))}
      <div style={{ display: 'flex', width: '100%' }}>

        <div style={{ width: hideApprovers ? '100%' : '70%' }} className='flex-column'>
          <div className='r-jsb m-t-30 p-r-10 p-b-20'>
            <Heading>Leave Requests</Heading>

            <div style={{ gap: 10 }} className='r-jsb m-t-10 display-flex'>
              <Popover
                content={yearPopoverContent}
                trigger="click"
                open={yearOpen}
                onOpenChange={setYearOpen}
                placement="topLeft"
                overlayClassName="leave-picker-popover"
              >
                <div className={`leave-filter-pill ${yearOpen ? 'active' : ''}`}>
                  <span>{isYear}</span>
                  <FiChevronDown className="custom-icon" />
                </div>
              </Popover>
              {/* Month picker — not shown when yearOnly (Users V2 shows the whole year). */}
              {!yearOnly && (
                <Popover
                  content={monthPopoverContent}
                  trigger="click"
                  open={monthOpen}
                  onOpenChange={setMonthOpen}
                  placement="topLeft"
                  overlayClassName="leave-picker-popover"
                >
                  <div className={`leave-filter-pill ${monthOpen ? 'active' : ''}`}>
                    <span>{isMonth}</span>
                    <FiChevronDown className="custom-icon" />
                  </div>
                </Popover>
              )}
              <div className='r-ac'>
                {((user?.privilage?.includes('SuperAdmin') || user?.privilage?.includes('LeaveManager')) || !user?.role?.includes("Student")) ? (
                  <Button type="primary" style={{ width: '120px' }} onClick={() => setMarkLeaveModal(true)}>
                    {(user?.privilage?.includes('SuperAdmin') || user?.privilage?.includes('LeaveManager') && (specificUserId != user?.id)) ? 'MARK LEAVE' : 'APPLY LEAVE'}
                  </Button>
                ) : null}
              </div>
            </div>
          </div>

          <div className=" m-b-10 approvers-container">
            <Spin spinning={tableLoader}>
              <LeaveTable
                setDeleteLeaveModal={setDeleteLeaveModal}
                handleEditLeave={handleEditLeave}
                selectedRowKeys={selectedRowKeys}
                setIdToDelete={setIdToDelete}
                user={user}
                setMultiLeaveDeleteModal={setMultiLeaveDeleteModal}
                setActionObject={setActionObject}
                setReqTitleModal={setReqTitleModal}
                toggleConfirmationModal={toggleConfirmationModal}
                leaveOrAttendanceRequestActions={leaveOrAttendanceRequestActions}
                fromWhere="leave"
                setSelectedLeaveRow={setSelectedLeaveRow}
                setIsEdit={setIsEdit}
                leaveDetails_V3={leaveDetails_V3}
                onRow={onRow}
                columns={columns(setDeleteLeaveModal, handleEditLeave, selectedRowKeys, setIdToDelete, user, setMultiLeaveDeleteModal, setActionObject, setReqTitleModal, toggleConfirmationModal, leaveOrAttendanceRequestActions, 'leave', setSelectedLeaveRow, setIsEdit
                )}
              />
            </Spin>
          </div>
        </div>

        {/* Approvers List Section — not shown at all when hideApprovers
          (e.g. from the staff-profile page's Leaves tab); the left column
          above takes the full width instead. */}
        {!hideApprovers && (
          <div style={{ width: '30%' }} className='p-10 flex-column'>
            <div className='r-jsb m-t-30 p-r-15 p-b-15'>
              <Heading>Approvers</Heading>
              <div className='r-ac'>
                {((user?.privilage?.includes('SuperAdmin') || user?.privilage?.includes('LeaveManager')) || !user?.role?.includes("Student")) ? (
                  (user?.privilage?.includes('SuperAdmin') || user?.privilage?.includes('LeaveManager')) &&
                  <Button type="link" style={{ marginRight: 20, fontSize: 14 }} onClick={() => { setEditApproverModal(true); setSelectedUserId(specificUserProfile.id); }} icon={<EditOutlined />}>
                    EDIT
                  </Button>
                ) : null}
              </div>
            </div>

            <div className="approvers-container">
              <div style={{ display: 'flex', justifyContent: 'center', }}>Will be implemented in the next version</div>
              {/* {approversList.map((approver, index) => (
              <Row key={approver.id} gutter={[16, 16]} className="approver-row" align="middle">
                <Row className="approver-full-width">
                  <Col>
                    <Title2 color={Color.aeGrey}>Approver {approver.index}</Title2>
                  </Col>
                </Row>
                <Row span={18} className="approver-info-row">
                  <Col xs={5} sm={5} md={4} lg={3} xl={2} className='r-ac-jcc'>
                    <UserAvatar
                      size="4vmin"
                      shape="circle"
                      name={approver.name}
                      img={approver.image === "default.png" ? "" : `${Api._s3_url}${approver.image}`}
                    />
                  </Col>
                  <Col xs={15} sm={15} md={16} lg={17} xl={18}>
                    <Row>
                      <Title2 color={Color.black} className="approver-name">{approver.name}</Title2>
                    </Row>
                    <Row>
                      <Title2 color={Color.darkGrey} className="approver-details text-overflow-ellipsis">
                        {approver.role} | {approver.phone}
                      </Title2>
                    </Row>
                  </Col>
                </Row>
                {index < approversList.length - 1 && (
                  <div className="approver-divider" />
                )}
              </Row>
            ))} */}
            </div>

          </div>
        )}
      </div>

      {editApproverModal && <EditApproversDrawer visible={editApproverModal} onClose={() => setEditApproverModal(false)} selectedUserId={selectedUserId} searchUsers={searchUsers} searchedTeachers={searchedTeachers} postEditApprovers={postEditApprovers} setPageLoader={setPageLoader} userDetails={specificUserProfile} leaveDetails_V3={leaveDetails_V3} getLeavesData={getLeavesData} isMonth={isMonth} isYear={isYear} />}
      {markLeaveModal &&
        <MarkLeaveDrawer visible={markLeaveModal} onClose={handleCloseMarkLeaveModal} leaveFormDetails={leaveFormDetails} setLeaveDetails={setLeaveDetails} pagecallback={setPageLoader} setPageLoader={setPageLoader} fromWhere={fromWhere} isEdit={isEdit} setIsEdit={setIsEdit} specificUserProfile={specificUserProfile} specificUserId={specificUserId} leaveDetails_V3={leaveDetails_V3} user={user} />
      }
      {deleteLeaveModal &&
        <ConfirmLeaveDelete visible={deleteLeaveModal} onClose={() => setDeleteLeaveModal(false)} onDelete={handleDelete} deleteLoader={deleteLoader} />
      }
      {editLeaveModal &&
        <EditLeaveDrawer visible={editLeaveModal} onClose={() => setEditLeaveModal(false)} leaveDetails_V3={leaveDetails_V3} getLeavesData={getLeavesData} fromWhere={fromWhere} />
      }
      {multiLeaveDeleteModal &&
        <MultipleLeaveDeleteModal visible={multiLeaveDeleteModal} data={selectedLeaveRow} loading={deleteLoader} deleteHistory={deleteHistory} setMultiLeaveDeleteModal={setMultiLeaveDeleteModal} setDeleteLoader={setDeleteLoader} setSelectedRowKeys={setSelectedRowKeys} />
      }
      {openLeaveRequestModal &&
        <LeaveRequestsModal open={openLeaveRequestModal} onClose={() => setLeaveRequestModal(false)} data={selectedLeaveRow} onActionSuccess={() => fetchLeaves()} user={user} />
      }
      <RequestsConfirmationModal open={openConfirmationModal} onCancel={() => toggleConfirmationModal(false)} title={reqModalTitle} onYes={() => { handleReqActionButton() }} />
      <PageDataLoader visible={pageLoader} />
    </div>
  );
}


const mapStateToProps = (state) => {
  const { user } = state.session || {};
  const { usersNameContainer } = state.attendanceNew || {};
  const { leaveDetails_V3 = {}, leaveTypes = {}, othersSelectedTab } = state.userManagementV3 || {};
  const { specificUserId, searchedTeachers } = state.userManagementV2 || {};
  return {
    leaveDetails_V3, leaveTypes, user, othersSelectedTab, usersNameContainer, specificUserId, searchedTeachers
  }
}

const mapDispatchToProps = (dispatch) => ({
  getLeavesData: (params, callback) => dispatch(getLeavesData(params, callback)),
  getLeavesTypes: (callback) => dispatch(getLeavesTypes(callback)),
  searchUsers: (params, callback) => dispatch(searchUsers(params, callback)),
  postEditApprovers: (params, callback, successCallback) => dispatch(postEditApprovers(params, callback, successCallback)),
  deleteHistory: (params, callback, successCallback, fromWhere) => dispatch(deleteHistory(params, callback, successCallback, fromWhere)),
  leaveOrAttendanceRequestActions: (params, callback, pagecallback, successCallback) => dispatch(leaveOrAttendanceRequestActions(params, callback, pagecallback, successCallback)),
})
export default connect(mapStateToProps, mapDispatchToProps)(profileLeaves);


